import { Card, Grid, Icon, IconButton, Table, TableBody, TableCell, TableHead, TableRow, TableContainer, MenuItem, Select, FormControl, Dialog, DialogTitle, DialogContent, DialogActions, Switch, Autocomplete, TextField, List, ListItem, ListItemButton, ListItemIcon, Checkbox, ListItemText, CircularProgress, Paper } from "@mui/material";
import MDBox from "components/MDBox";
import MDButton from "components/MDButton";
import MDInput from "components/MDInput";
import MDTypography from "components/MDTypography";
import MDBadge from "components/MDBadge";
import { useEffect, useState } from "react";
import { get, post, postNoResponse } from "utils/WebService";
import MDSnackbar from "components/MDSnackbar";
import { useMaterialUIController } from "context";

export default function RegrasFilaCreateEdit({ recordId, closeCreateEdit }) {
    const [controller] = useMaterialUIController();
    const { darkMode } = controller;
    const [nome, setNome] = useState("");
    const [ativo, setAtivo] = useState(true);
    const [itens, setItens] = useState([]);
    const [statusOptions, setStatusOptions] = useState([]);
    const [pessoaOptions, setPessoaOptions] = useState([]);
    
    const [successSB, setSuccessSB] = useState(false);
    const [errorSB, setErrorSB] = useState(false);
    const [sbMessage, setSbMessage] = useState("");

    const [statusModalOpen, setStatusModalOpen] = useState(false);
    const [pessoaModalOpen, setPessoaModalOpen] = useState(false);
    const [currentEditIndex, setCurrentEditIndex] = useState(null);
    const [statusSearch, setStatusSearch] = useState("");
    const [pessoaSearch, setPessoaSearch] = useState("");

    const [clienteModoGeral, setClienteModoGeral] = useState(false);
    const [globalPessoasIds, setGlobalPessoasIds] = useState([]);
    const [globalPessoasInverter, setGlobalPessoasInverter] = useState(false);

    const [casosModalOpen, setCasosModalOpen] = useState(false);
    const [casosList, setCasosList] = useState([]);
    const [loadingCasos, setLoadingCasos] = useState(false);

    const [detailModalOpen, setDetailModalOpen] = useState(false);
    const [devedorDetail, setDevedorDetail] = useState(null);
    const [loadingDetail, setLoadingDetail] = useState(false);

    const normalizeItens = (rawItens) => {
        if (!Array.isArray(rawItens)) return [];
        return rawItens.map(it => ({
            id: it.id || it.cvId,
            statusFichaIds: Array.isArray(it.statusFichaIds) 
                ? it.statusFichaIds 
                : (it.ccStatusFichasIds ? it.ccStatusFichasIds.split(",") : []),
            statusInverter: it.statusInverter ?? it.cbStatusInverter ?? false,
            pessoasIds: Array.isArray(it.pessoasIds) 
                ? it.pessoasIds 
                : (it.ccPessoasIds ? it.ccPessoasIds.split(",") : []),
            pessoasInverter: it.pessoasInverter ?? it.cbPessoasInverter ?? false,
            regraAgendamento: it.regraAgendamento ?? it.cvRegraAgendamento ?? null,
            saldoMin: it.saldoMin ?? it.cnSaldoMin ?? "",
            saldoMax: it.saldoMax ?? it.cnSaldoMax ?? "",
            pesoOrdem: it.pesoOrdem ?? it.cvPesoOrdem ?? "",
            totalCasos: it.totalCasos ?? 0
        }));
    };

    const applyRuleData = (data) => {
        if (!data) return;
        setNome(data.nome || data.ccNome || "");
        setAtivo(data.ativo ?? data.cvIndAtivo ?? true);
        const loadedItens = normalizeItens(data.itens || data.tbRegraFilaItems || []);
        setItens(loadedItens);

        if (loadedItens.length > 0) {
            const firstPessoas = loadedItens[0].pessoasIds || [];
            const firstInverter = loadedItens[0].pessoasInverter || false;
            const allSame = loadedItens.every(it => {
                const currentPessoas = it.pessoasIds || [];
                if ((it.pessoasInverter || false) !== firstInverter) return false;
                if (currentPessoas.length !== firstPessoas.length) return false;
                return currentPessoas.every(id => firstPessoas.includes(id));
            });

            if (allSame && firstPessoas.length > 0) {
                setClienteModoGeral(true);
                setGlobalPessoasIds(firstPessoas);
                setGlobalPessoasInverter(firstInverter);
            } else {
                setClienteModoGeral(false);
                setGlobalPessoasIds([]);
                setGlobalPessoasInverter(false);
            }
        } else {
            setClienteModoGeral(false);
            setGlobalPessoasIds([]);
            setGlobalPessoasInverter(false);
        }
    };

    const getNumericId = (val) => {
        if (!val) return null;
        if (typeof val === "object") return val.id || val.cvId || null;
        const parsed = parseInt(val, 10);
        return isNaN(parsed) ? null : parsed;
    };

    useEffect(() => {
        get("/status-ficha/list-all").then((res) => {
            setStatusOptions(res.list || res.data || (Array.isArray(res) ? res : []));
        });

        get("/pessoa/list-all").then((res) => {
            setPessoaOptions(res.list || res.data || (Array.isArray(res) ? res : []));
        });

        if (recordId) {
            // Se recordId for o próprio objeto da regra (veio da tabela), carrega imediatamente
            if (typeof recordId === "object" && recordId !== null) {
                applyRuleData(recordId);
            }

            const cleanId = getNumericId(recordId);

            if (cleanId) {
                get(`/regra-fila/by-id/${cleanId}`).then((res) => {
                    if (res) {
                        applyRuleData(res);
                    }
                }).catch((err) => {
                    console.error("Erro ao buscar regra por id:", err);
                });
            }
        } else {
            setNome("");
            setAtivo(true);
            setItens([]);
            setClienteModoGeral(false);
            setGlobalPessoasIds([]);
            setGlobalPessoasInverter(false);
        }
    }, [recordId]);

    const sanitizeItemPayload = (item, idx) => {
        const parsedId = getNumericId(item?.id);
        const statusIds = Array.isArray(item?.statusFichaIds)
            ? item.statusFichaIds.map(s => String(typeof s === "object" ? (s.id || s.cvId || s) : s))
            : [];
        const pIds = clienteModoGeral
            ? (globalPessoasIds || []).map(p => String(typeof p === "object" ? (p.id || p.cvId || p) : p))
            : (Array.isArray(item?.pessoasIds) ? item.pessoasIds.map(p => String(typeof p === "object" ? (p.id || p.cvId || p) : p)) : []);

        let age = null;
        if (item?.regraAgendamento !== null && item?.regraAgendamento !== undefined && item?.regraAgendamento !== "") {
            const pAge = parseInt(item.regraAgendamento, 10);
            if (!isNaN(pAge)) age = pAge;
        }

        let sMin = null;
        if (item?.saldoMin !== null && item?.saldoMin !== undefined && item?.saldoMin !== "") {
            const pMin = parseFloat(item.saldoMin);
            if (!isNaN(pMin)) sMin = pMin;
        }

        let sMax = null;
        if (item?.saldoMax !== null && item?.saldoMax !== undefined && item?.saldoMax !== "") {
            const pMax = parseFloat(item.saldoMax);
            if (!isNaN(pMax)) sMax = pMax;
        }

        let pOrdem = (idx !== undefined ? idx + 1 : 1);
        if (item?.pesoOrdem !== null && item?.pesoOrdem !== undefined && item?.pesoOrdem !== "") {
            const pOrd = parseInt(item.pesoOrdem, 10);
            if (!isNaN(pOrd)) pOrdem = pOrd;
        }

        return {
            id: parsedId,
            statusFichaIds: statusIds,
            statusInverter: !!item?.statusInverter,
            pessoasIds: pIds,
            pessoasInverter: clienteModoGeral ? !!globalPessoasInverter : !!item?.pessoasInverter,
            regraAgendamento: age,
            saldoMin: sMin,
            saldoMax: sMax,
            pesoOrdem: pOrdem
        };
    };

    const handleSave = () => {
        if (!nome) {
            setSbMessage("Informe o nome da regra.");
            setErrorSB(true);
            return;
        }

        const payload = {
            id: getNumericId(recordId),
            nome,
            ativo,
            itens: itens.map((item, idx) => sanitizeItemPayload(item, idx))
        };

        postNoResponse("/regra-fila/save", payload).then(() => {
            setSbMessage("Regra salva com sucesso!");
            setSuccessSB(true);
            setTimeout(() => {
                closeCreateEdit();
            }, 1000);
        }).catch((err) => {
            const errorMsg = err?.response?.data?.message || err?.data?.message || err?.message || "Erro ao salvar regra.";
            setSbMessage(errorMsg);
            setErrorSB(true);
        });
    };

    const handleAddItem = () => {
        setItens([...itens, {
            statusFichaIds: [],
            statusInverter: false,
            pessoasIds: clienteModoGeral ? [...globalPessoasIds] : [],
            pessoasInverter: clienteModoGeral ? globalPessoasInverter : false,
            regraAgendamento: null,
            saldoMin: "",
            saldoMax: "",
            pesoOrdem: itens.length + 1
        }]);
    };

    const handlePessoaToggleGlobal = (pId) => {
        const isChecked = globalPessoasIds.includes(pId);
        const newIds = isChecked ? globalPessoasIds.filter(id => id !== pId) : [...globalPessoasIds, pId];
        setGlobalPessoasIds(newIds);
        setItens(prev => prev.map(item => ({ ...item, pessoasIds: newIds })));
    };

    const handleGlobalPessoasInverterChange = (checked) => {
        setGlobalPessoasInverter(checked);
        setItens(prev => prev.map(item => ({ ...item, pessoasInverter: checked })));
    };

    const handleItemChange = (index, field, value) => {
        const newItens = [...itens];
        newItens[index][field] = value;
        setItens(newItens);
    };

    const handleRemoveItem = (index) => {
        const newItens = itens.filter((_, i) => i !== index);
        setItens(newItens);
    };

    const handleOpenCasosModal = (index) => {
        const item = itens[index];
        if (!item) return;
        setCasosModalOpen(true);
        setLoadingCasos(true);
        setCasosList([]);

        const payloadItem = sanitizeItemPayload(item, index);

        post("/regra-fila/listar-devedores-item", payloadItem).then((res) => {
            const list = Array.isArray(res) ? res : (res?.data || res?.list || []);
            setCasosList(list);
            setLoadingCasos(false);
        }).catch((err) => {
            const errorMsg = err?.response?.data?.message || err?.data?.message || err?.message || "Erro ao buscar devedores do item.";
            setSbMessage(errorMsg);
            setErrorSB(true);
            setLoadingCasos(false);
        });
    };

    const handleOpenDetailModal = (devId) => {
        if (!devId) return;
        setDetailModalOpen(true);
        setLoadingDetail(true);
        setDevedorDetail(null);
        get(`/devedor/by-id/${devId}`).then((res) => {
            setDevedorDetail(res || null);
            setLoadingDetail(false);
        }).catch(() => {
            setLoadingDetail(false);
        });
    };

    const [loadingRecalcular, setLoadingRecalcular] = useState(false);

    const handleRecalcularContadores = () => {
        setLoadingRecalcular(true);
        const dto = {
            id: getNumericId(recordId),
            nome: nome || "Nova Regra",
            ativo: ativo !== undefined ? ativo : true,
            itens: itens.map((item, idx) => sanitizeItemPayload(item, idx))
        };

        post("/regra-fila/recalcular-contadores", dto).then((res) => {
            const updatedItens = Array.isArray(res) ? res : (res?.data || res?.list || []);
            if (updatedItens.length > 0) {
                setItens(prevItens => prevItens.map((item, idx) => {
                    const match = updatedItens[idx];
                    return {
                        ...item,
                        totalCasos: match && match.totalCasos != null ? match.totalCasos : 0
                    };
                }));
            }
            setSbMessage("Contadores recalculados com sucesso!");
            setSuccessSB(true);
            setLoadingRecalcular(false);
        }).catch((err) => {
            const errorMsg = err?.response?.data?.message || err?.data?.message || err?.message || "Erro ao recalcular contadores.";
            setSbMessage(errorMsg);
            setErrorSB(true);
            setLoadingRecalcular(false);
        });
    };

    const openStatusModal = (index) => {
        setCurrentEditIndex(index);
        setStatusSearch("");
        setStatusModalOpen(true);
    };

    const closeStatusModal = () => {
        setStatusModalOpen(false);
        if (!pessoaModalOpen) setCurrentEditIndex(null);
    };

    const openPessoaModal = (index) => {
        setCurrentEditIndex(index);
        setPessoaSearch("");
        setPessoaModalOpen(true);
    };

    const closePessoaModal = () => {
        setPessoaModalOpen(false);
        if (!statusModalOpen) setCurrentEditIndex(null);
    };

    const getStatusLabel = (ids) => {
        if (!ids || ids.length === 0) return "Selecionar Status";
        const names = ids.map(id => {
            const st = statusOptions.find(opt => opt.id === id);
            return st ? (st.ccNome || st.id) : id;
        });
        return names.join(', ');
    };

    const getPessoasLabel = (ids) => {
        if (!ids || ids.length === 0) return "Selecionar Cliente";
        const names = ids.map(id => {
            const p = pessoaOptions.find(opt => opt.id === id);
            return p ? (p.nomeComum || p.nomeCompleto || p.id) : id;
        });
        return names.join(', ');
    };

    return (
        <Card id="regra-fila-form">
            <MDBox display="flex" alignItems="center" p={3}>
                <IconButton onClick={closeCreateEdit} sx={{ p: 0, paddingRight: 1 }}>
                    <MDTypography variant="h5"><Icon>arrow_back</Icon></MDTypography>
                </IconButton>
                <MDTypography variant="h6">{recordId ? "Editar Regra" : "Nova Regra"}</MDTypography>
            </MDBox>

            <MDBox component="section" px={3} pb={3}>
                <Grid container spacing={3}>
                    <Grid item xs={12} sm={8}>
                        <MDInput 
                            fullWidth 
                            name="nome" 
                            label="Nome da Regra" 
                            variant="standard" 
                            value={nome} 
                            onChange={(e) => setNome(e.target.value)} 
                            required 
                        />
                    </Grid>
                    <Grid item xs={12} sm={4} display="flex" alignItems="center" pt={4} pl={2}>
                        <MDTypography variant="button" fontWeight="regular" color="text">Inativo</MDTypography>
                        <Switch checked={ativo} onChange={(e) => setAtivo(e.target.checked)} />
                        <MDTypography variant="button" fontWeight="regular" color="text">Ativo</MDTypography>
                    </Grid>
                </Grid>

                {/* Seção de Seleção de Modo do Cliente */}
                <MDBox mt={3} p={2} sx={{ border: darkMode ? "1px solid #606472" : "1px solid #e0e0e0", borderRadius: "8px", backgroundColor: darkMode ? "#4B4F5C" : "#fafafa" }}>
                    <Grid container spacing={2} alignItems="center">
                        <Grid item xs={12} md={clienteModoGeral ? 5 : 12}>
                            <MDBox display="flex" alignItems="center">
                                <Switch 
                                    checked={clienteModoGeral} 
                                    onChange={(e) => {
                                        const isGeral = e.target.checked;
                                        setClienteModoGeral(isGeral);
                                        if (isGeral) {
                                            const firstPessoas = itens[0]?.pessoasIds || globalPessoasIds || [];
                                            const firstInverter = itens[0]?.pessoasInverter || globalPessoasInverter || false;
                                            setGlobalPessoasIds(firstPessoas);
                                            setGlobalPessoasInverter(firstInverter);
                                            setItens(prev => prev.map(item => ({
                                                ...item,
                                                pessoasIds: [...firstPessoas],
                                                pessoasInverter: firstInverter
                                            })));
                                        }
                                    }} 
                                />
                                <MDBox ml={1}>
                                    <MDTypography variant="button" fontWeight="bold" color="text">
                                        Aplicar Cliente Geral para Todas as Regras
                                    </MDTypography>
                                    <MDTypography variant="caption" color="text" display="block">
                                        {clienteModoGeral 
                                            ? "Um único cliente selecionado no topo é aplicado a todas as regras abaixo" 
                                            : "Modo atual: cada regra possui seu próprio cliente configurado individualmente"}
                                    </MDTypography>
                                </MDBox>
                            </MDBox>
                        </Grid>

                        {clienteModoGeral && (
                            <Grid item xs={12} md={7} display="flex" alignItems="center" gap={2}>
                                <MDBox flexGrow={1}>
                                    <MDTypography variant="caption" fontWeight="bold" color="text" display="block" mb={0.5}>
                                        Cliente(s) Geral:
                                    </MDTypography>
                                    <MDButton 
                                        variant="outlined" 
                                        color="info" 
                                        size="small" 
                                        onClick={() => openPessoaModal('global')}
                                        fullWidth
                                        sx={{ textTransform: 'none', whiteSpace: 'normal', minHeight: 'auto', py: 1 }}
                                    >
                                        {getPessoasLabel(globalPessoasIds)}
                                    </MDButton>
                                </MDBox>

                                <MDBox display="flex" alignItems="center" pt={2.5}>
                                    <Switch 
                                        checked={globalPessoasInverter} 
                                        onChange={(e) => handleGlobalPessoasInverterChange(e.target.checked)} 
                                    />
                                    <MDTypography variant="caption" color="text" sx={{ ml: 1, lineHeight: 1.2 }}>
                                        Inverter Regra<br/>(NÃO é este)
                                    </MDTypography>
                                </MDBox>
                            </Grid>
                        )}
                    </Grid>
                </MDBox>

                <MDBox mt={4} mb={2} display="flex" justifyContent="space-between" alignItems="center">
                    <MDBox display="flex" alignItems="center" gap={2}>
                        <MDTypography variant="h6" fontWeight="medium">Regras</MDTypography>
                        <MDBadge 
                            badgeContent={`Total de Casos: ${itens.reduce((acc, curr) => acc + (parseInt(curr.totalCasos, 10) || 0), 0)}`} 
                            color="info" 
                            variant="gradient" 
                            size="lg" 
                            container 
                            sx={{ px: 1.5, py: 0.5, fontSize: "0.85rem", borderRadius: "16px" }} 
                        />
                    </MDBox>
                    <MDBox display="flex" gap={1}>
                        <MDButton 
                            variant="gradient" 
                            color="info" 
                            onClick={handleRecalcularContadores}
                            disabled={loadingRecalcular || itens.length === 0}
                            size="small"
                        >
                            {loadingRecalcular ? (
                                <CircularProgress size={16} color="white" />
                            ) : (
                                <>
                                    <Icon sx={{ mr: 0.5 }}>calculate</Icon> Recalcular Contadores
                                </>
                            )}
                        </MDButton>
                        <MDButton variant="outlined" color="info" onClick={handleAddItem} size="small">
                            <Icon>add</Icon>&nbsp; Adicionar Regra
                        </MDButton>
                    </MDBox>
                </MDBox>

                <TableContainer sx={{ maxHeight: "calc(100vh - 420px)", minHeight: "280px", overflowY: "auto", borderRadius: "8px", border: darkMode ? "1px solid #505461" : "1px solid #e0e0e0", width: "100%" }}>
                    <Table stickyHeader sx={{ width: "100%", tableLayout: "fixed", borderCollapse: "collapse" }}>
                        <TableHead sx={{ display: "table-header-group", width: "100%" }}>
                            <TableRow sx={{ display: "table-row", width: "100%", backgroundColor: darkMode ? "#4B4F5C" : "#fafafa" }}>
                                <TableCell sx={{ width: "22%", display: "table-cell", backgroundColor: darkMode ? "#4B4F5C" : "#fafafa", zIndex: 10, verticalAlign: "top", pt: 1.5, px: 1 }}>
                                    <MDTypography variant="caption" fontWeight="bold" color="text" sx={{ whiteSpace: "nowrap" }}>Status da Ficha</MDTypography>
                                </TableCell>
                                <TableCell sx={{ width: "22%", display: "table-cell", backgroundColor: darkMode ? "#4B4F5C" : "#fafafa", zIndex: 10, verticalAlign: "top", pt: 1.5, px: 1 }}>
                                    <MDTypography variant="caption" fontWeight="bold" color="text" sx={{ whiteSpace: "nowrap" }}>Cliente(s)</MDTypography>
                                </TableCell>
                                <TableCell sx={{ width: "16%", display: "table-cell", backgroundColor: darkMode ? "#4B4F5C" : "#fafafa", zIndex: 10, verticalAlign: "top", pt: 1.5, px: 1 }}>
                                    <MDTypography variant="caption" fontWeight="bold" color="text" sx={{ whiteSpace: "nowrap" }}>Agendamento</MDTypography>
                                </TableCell>
                                <TableCell sx={{ width: "11%", display: "table-cell", backgroundColor: darkMode ? "#4B4F5C" : "#fafafa", zIndex: 10, verticalAlign: "top", pt: 1.5, px: 1 }}>
                                    <MDTypography variant="caption" fontWeight="bold" color="text" sx={{ whiteSpace: "nowrap" }}>Saldo Min (R$)</MDTypography>
                                </TableCell>
                                <TableCell sx={{ width: "11%", display: "table-cell", backgroundColor: darkMode ? "#4B4F5C" : "#fafafa", zIndex: 10, verticalAlign: "top", pt: 1.5, px: 1 }}>
                                    <MDTypography variant="caption" fontWeight="bold" color="text" sx={{ whiteSpace: "nowrap" }}>Saldo Max (R$)</MDTypography>
                                </TableCell>
                                <TableCell sx={{ width: "8%", display: "table-cell", backgroundColor: darkMode ? "#4B4F5C" : "#fafafa", zIndex: 10, verticalAlign: "top", pt: 1.5, px: 1 }}>
                                    <MDTypography variant="caption" fontWeight="bold" color="text" sx={{ whiteSpace: "nowrap" }}>Prioridade</MDTypography>
                                </TableCell>
                                <TableCell align="center" sx={{ width: "5%", display: "table-cell", backgroundColor: darkMode ? "#4B4F5C" : "#fafafa", zIndex: 10, verticalAlign: "top", pt: 1.5, px: 1 }}>
                                    <MDTypography variant="caption" fontWeight="bold" color="text" sx={{ whiteSpace: "nowrap" }}>Casos</MDTypography>
                                </TableCell>
                                <TableCell align="center" sx={{ width: "5%", display: "table-cell", backgroundColor: darkMode ? "#4B4F5C" : "#fafafa", zIndex: 10, verticalAlign: "top", pt: 1.5, px: 1 }}>
                                    <MDTypography variant="caption" fontWeight="bold" color="text" sx={{ whiteSpace: "nowrap" }}>Ação</MDTypography>
                                </TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody sx={{ display: "table-row-group", width: "100%" }}>
                            {itens.length === 0 && (
                                <TableRow sx={{ display: "table-row" }}>
                                    <TableCell colSpan={8} align="center" sx={{ display: "table-cell" }}>
                                        <MDTypography variant="caption" color="text">Nenhuma regra adicionada.</MDTypography>
                                    </TableCell>
                                </TableRow>
                            )}
                            {itens.map((item, index) => (
                                <TableRow key={index} sx={{ display: "table-row", width: "100%" }}>
                                    <TableCell sx={{ width: "22%", display: "table-cell", whiteSpace: 'normal', verticalAlign: 'top', pt: 1.5, px: 1 }}>
                                        <MDBox display="flex" flexDirection="column" gap={1}>
                                            <MDButton 
                                                variant="outlined" 
                                                color="info" 
                                                size="small" 
                                                onClick={() => openStatusModal(index)}
                                                fullWidth
                                                sx={{ textTransform: 'none', whiteSpace: 'normal', minHeight: 'auto', py: 1 }}
                                            >
                                                {getStatusLabel(item.statusFichaIds)}
                                            </MDButton>
                                            <MDBox display="flex" alignItems="center">
                                                <Switch 
                                                    checked={item.statusInverter || false} 
                                                    onChange={(e) => handleItemChange(index, "statusInverter", e.target.checked)} 
                                                />
                                                <MDTypography variant="caption" color="text" sx={{ ml: 1, lineHeight: 1.2 }}>Inverter Regra<br/>(NÃO está nestes)</MDTypography>
                                            </MDBox>
                                        </MDBox>
                                    </TableCell>
                                    <TableCell sx={{ width: "22%", display: "table-cell", whiteSpace: 'normal', verticalAlign: 'top', pt: 1.5, px: 1 }}>
                                        {clienteModoGeral ? (
                                            <MDBox display="flex" flexDirection="column" gap={0.5} opacity={0.85}>
                                                <MDButton 
                                                    variant="outlined" 
                                                    color="secondary" 
                                                    size="small" 
                                                    disabled
                                                    fullWidth
                                                    sx={{ textTransform: 'none', whiteSpace: 'normal', minHeight: 'auto', py: 1 }}
                                                >
                                                    {getPessoasLabel(globalPessoasIds)}
                                                </MDButton>
                                                <MDTypography variant="caption" color="text" sx={{ fontStyle: 'italic', fontSize: '0.75rem' }}>
                                                    (Definido no Cliente Geral)
                                                </MDTypography>
                                            </MDBox>
                                        ) : (
                                            <MDBox display="flex" flexDirection="column" gap={1}>
                                                <MDButton 
                                                    variant="outlined" 
                                                    color="info" 
                                                    size="small" 
                                                    onClick={() => openPessoaModal(index)}
                                                    fullWidth
                                                    sx={{ textTransform: 'none', whiteSpace: 'normal', minHeight: 'auto', py: 1 }}
                                                >
                                                    {getPessoasLabel(item.pessoasIds)}
                                                </MDButton>
                                                <MDBox display="flex" alignItems="center">
                                                    <Switch 
                                                        checked={item.pessoasInverter || false} 
                                                        onChange={(e) => handleItemChange(index, "pessoasInverter", e.target.checked)} 
                                                    />
                                                    <MDTypography variant="caption" color="text" sx={{ ml: 1, lineHeight: 1.2 }}>Inverter Regra<br/>(NÃO é este)</MDTypography>
                                                </MDBox>
                                            </MDBox>
                                        )}
                                    </TableCell>
                                    <TableCell sx={{ width: "16%", display: "table-cell", verticalAlign: 'top', pt: 1.5, px: 1 }}>
                                        <FormControl fullWidth variant="standard" sx={{ mt: 0.5 }}>
                                            <Select
                                                value={item.regraAgendamento || ""}
                                                onChange={(e) => handleItemChange(index, "regraAgendamento", e.target.value)}
                                                displayEmpty
                                                sx={{ py: 0.5 }}
                                            >
                                                <MenuItem value=""><MDTypography variant="button" fontWeight="regular">Ignorar</MDTypography></MenuItem>
                                                <MenuItem value={1}><MDTypography variant="button" fontWeight="regular">Atrasado/Hoje ({"<="})</MDTypography></MenuItem>
                                                <MenuItem value={4}><MDTypography variant="button" fontWeight="regular">Apenas Hoje (=)</MDTypography></MenuItem>
                                                <MenuItem value={2}><MDTypography variant="button" fontWeight="regular">Futuro ({">"})</MDTypography></MenuItem>
                                                <MenuItem value={3}><MDTypography variant="button" fontWeight="regular">Sem agendamento</MDTypography></MenuItem>
                                            </Select>
                                        </FormControl>
                                    </TableCell>
                                    <TableCell sx={{ width: "11%", display: "table-cell", verticalAlign: 'top', pt: 1.5, px: 1 }}>
                                        <MDInput
                                            variant="standard"
                                            type="number"
                                            value={item.saldoMin || ""}
                                            onChange={(e) => handleItemChange(index, "saldoMin", e.target.value)}
                                            fullWidth
                                        />
                                    </TableCell>
                                    <TableCell sx={{ width: "11%", display: "table-cell", verticalAlign: 'top', pt: 1.5, px: 1 }}>
                                        <MDInput
                                            variant="standard"
                                            type="number"
                                            value={item.saldoMax || ""}
                                            onChange={(e) => handleItemChange(index, "saldoMax", e.target.value)}
                                            fullWidth
                                        />
                                    </TableCell>
                                    <TableCell sx={{ width: "8%", display: "table-cell", verticalAlign: 'top', pt: 1.5, px: 1 }}>
                                        <MDInput
                                            variant="standard"
                                            type="number"
                                            value={item.pesoOrdem || ""}
                                            onChange={(e) => handleItemChange(index, "pesoOrdem", e.target.value)}
                                            fullWidth
                                        />
                                    </TableCell>
                                    <TableCell align="center" sx={{ width: "5%", display: "table-cell", verticalAlign: 'top', pt: 2.0, px: 1 }}>
                                        <MDBox 
                                            px={1.5} 
                                            py={0.5} 
                                            onClick={() => handleOpenCasosModal(index)}
                                            sx={{ 
                                                backgroundColor: darkMode ? '#3A3D47' : '#e3f2fd', 
                                                borderRadius: '12px', 
                                                display: 'inline-block',
                                                minWidth: '45px',
                                                cursor: 'pointer',
                                                transition: 'transform 0.15s, background-color 0.15s',
                                                '&:hover': {
                                                    backgroundColor: darkMode ? '#4A4E5C' : '#bbdefb',
                                                    transform: 'scale(1.08)'
                                                }
                                            }}
                                        >
                                            <MDTypography variant="caption" fontWeight="bold" color="info">
                                                {item.totalCasos != null ? item.totalCasos : 0}
                                            </MDTypography>
                                        </MDBox>
                                    </TableCell>
                                    <TableCell align="center" sx={{ width: "5%", display: "table-cell", verticalAlign: 'top', pt: 1.5, px: 1 }}>
                                        <IconButton color="error" onClick={() => handleRemoveItem(index)}>
                                            <Icon>delete</Icon>
                                        </IconButton>
                                    </TableCell>
                                </TableRow>
                            ))}
                        </TableBody>
                    </Table>
                </TableContainer>

                <MDBox display="flex" justifyContent="flex-end" mt={3} pt={2} sx={{ borderTop: darkMode ? "1px solid #505461" : "1px solid #e0e0e0" }}>
                    <MDButton variant="outlined" color="secondary" onClick={closeCreateEdit} sx={{ mr: 2 }}>
                        Cancelar
                    </MDButton>
                    <MDButton variant="contained" color="info" onClick={handleSave}>
                        Salvar
                    </MDButton>
                </MDBox>
            </MDBox>

            <MDSnackbar
                color={successSB ? "success" : "error"}
                icon={successSB ? "check" : "warning"}
                title={successSB ? "Sucesso" : "Erro"}
                content={sbMessage}
                dateTime=""
                open={successSB || errorSB}
                onClose={() => { setSuccessSB(false); setErrorSB(false); }}
                close={() => { setSuccessSB(false); setErrorSB(false); }}
                bgWhite
            />

            <Dialog 
                open={statusModalOpen} 
                onClose={closeStatusModal} 
                fullWidth 
                maxWidth="sm"
                PaperProps={{
                    sx: {
                        backgroundColor: darkMode ? "#4B4F5C" : "#ffffff",
                        color: darkMode ? "#ffffff" : "inherit"
                    }
                }}
            >
                <DialogTitle sx={{ color: darkMode ? "#ffffff" : "inherit" }}>Selecionar Status da Ficha</DialogTitle>
                <DialogContent sx={{ minHeight: '300px', pt: 2 }}>
                    {currentEditIndex !== null && (
                        <MDBox>
                            <TextField 
                                variant="outlined" 
                                placeholder="Buscar status..." 
                                fullWidth
                                value={statusSearch}
                                onChange={(e) => setStatusSearch(e.target.value)}
                                sx={{ mb: 2 }}
                            />
                            <List sx={{ maxHeight: 300, overflow: 'auto', border: darkMode ? '1px solid #606472' : '1px solid #eee', borderRadius: '4px' }}>
                                {statusOptions.filter(st => {
                                    const nome = st.ccNome || String(st.id) || "";
                                    return nome.toLowerCase().includes(statusSearch.toLowerCase());
                                }).map(st => {
                                    const currentIds = itens[currentEditIndex]?.statusFichaIds || [];
                                    const isChecked = currentIds.includes(st.id);
                                    return (
                                        <ListItem key={st.id} disablePadding>
                                            <ListItemButton onClick={() => {
                                                const newIds = isChecked ? currentIds.filter(id => id !== st.id) : [...currentIds, st.id];
                                                handleItemChange(currentEditIndex, "statusFichaIds", newIds);
                                            }}>
                                                <ListItemIcon>
                                                    <Checkbox edge="start" checked={isChecked} tabIndex={-1} disableRipple />
                                                </ListItemIcon>
                                                <ListItemText primary={st.ccNome || st.id} sx={{ color: darkMode ? "#ffffff" : "inherit" }} />
                                            </ListItemButton>
                                        </ListItem>
                                    );
                                })}
                            </List>
                        </MDBox>
                    )}
                </DialogContent>
                <DialogActions>
                    <MDButton onClick={closeStatusModal} color="info">Concluído</MDButton>
                </DialogActions>
            </Dialog>

            <Dialog 
                open={pessoaModalOpen} 
                onClose={closePessoaModal} 
                fullWidth 
                maxWidth="sm"
                PaperProps={{
                    sx: {
                        backgroundColor: darkMode ? "#4B4F5C" : "#ffffff",
                        color: darkMode ? "#ffffff" : "inherit"
                    }
                }}
            >
                <DialogTitle sx={{ color: darkMode ? "#ffffff" : "inherit" }}>Selecionar Cliente(s)</DialogTitle>
                <DialogContent sx={{ minHeight: '300px', pt: 2 }}>
                    {currentEditIndex !== null && (
                        <MDBox>
                            <TextField 
                                variant="outlined" 
                                placeholder="Buscar cliente..." 
                                fullWidth
                                value={pessoaSearch}
                                onChange={(e) => setPessoaSearch(e.target.value)}
                                sx={{ mb: 2 }}
                            />
                            <List sx={{ maxHeight: 300, overflow: 'auto', border: darkMode ? '1px solid #606472' : '1px solid #eee', borderRadius: '4px' }}>
                                {pessoaOptions.filter(p => {
                                    const nome = p.nomeComum || p.nomeCompleto || String(p.id) || "";
                                    return nome.toLowerCase().includes(pessoaSearch.toLowerCase());
                                }).map(p => {
                                    const currentIds = currentEditIndex === 'global' 
                                        ? globalPessoasIds 
                                        : (itens[currentEditIndex]?.pessoasIds || []);
                                    const isChecked = currentIds.includes(p.id);
                                    return (
                                        <ListItem key={p.id} disablePadding>
                                            <ListItemButton onClick={() => {
                                                if (currentEditIndex === 'global') {
                                                    handlePessoaToggleGlobal(p.id);
                                                } else {
                                                    const newIds = isChecked ? currentIds.filter(id => id !== p.id) : [...currentIds, p.id];
                                                    handleItemChange(currentEditIndex, "pessoasIds", newIds);
                                                }
                                            }}>
                                                <ListItemIcon>
                                                    <Checkbox edge="start" checked={isChecked} tabIndex={-1} disableRipple />
                                                </ListItemIcon>
                                                <ListItemText primary={p.nomeComum || p.nomeCompleto || p.id} sx={{ color: darkMode ? "#ffffff" : "inherit" }} />
                                            </ListItemButton>
                                        </ListItem>
                                    );
                                })}
                            </List>
                        </MDBox>
                    )}
                </DialogContent>
                <DialogActions>
                    <MDButton onClick={closePessoaModal} color="info">Concluído</MDButton>
                </DialogActions>
            </Dialog>

            <Dialog 
                open={casosModalOpen} 
                onClose={() => setCasosModalOpen(false)}
                maxWidth="md"
                fullWidth
                PaperProps={{
                    sx: {
                        backgroundColor: darkMode ? "#4B4F5C" : "#ffffff",
                        color: darkMode ? "#ffffff" : "inherit"
                    }
                }}
            >
                <DialogTitle display="flex" justifyContent="space-between" alignItems="center">
                    <MDTypography variant="h6" color={darkMode ? "white" : "dark"}>
                        Casos Encontrados ({casosList.length})
                    </MDTypography>
                    <IconButton onClick={() => setCasosModalOpen(false)} size="small" sx={{ color: darkMode ? "#ffffff" : "inherit" }}>
                        <Icon>close</Icon>
                    </IconButton>
                </DialogTitle>
                <DialogContent dividers>
                    {loadingCasos ? (
                        <MDBox display="flex" justifyContent="center" py={4}>
                            <CircularProgress color="info" />
                        </MDBox>
                    ) : casosList.length === 0 ? (
                        <MDBox py={3} textAlign="center">
                            <MDTypography variant="button" color={darkMode ? "white" : "text"}>
                                Nenhum caso/devedor encontrado para esta regra.
                            </MDTypography>
                        </MDBox>
                    ) : (
                        <TableContainer component={Paper} elevation={0} sx={{ backgroundColor: 'transparent', boxShadow: 'none', width: '100%' }}>
                            <Table size="small" sx={{ width: "100%", tableLayout: "fixed", display: "table" }}>
                                <TableHead sx={{ display: "table-header-group", width: "100%", p: 0 }}>
                                    <TableRow sx={{ display: "table-row", width: "100%" }}>
                                        <TableCell sx={{ width: "20%", display: "table-cell", verticalAlign: "middle", py: 1.5, px: 1 }}>
                                            <MDTypography variant="caption" fontWeight="bold" color={darkMode ? "white" : "text"}>ID</MDTypography>
                                        </TableCell>
                                        <TableCell sx={{ width: "55%", display: "table-cell", verticalAlign: "middle", py: 1.5, px: 1 }}>
                                            <MDTypography variant="caption" fontWeight="bold" color={darkMode ? "white" : "text"}>Devedor</MDTypography>
                                        </TableCell>
                                        <TableCell sx={{ width: "25%", display: "table-cell", verticalAlign: "middle", py: 1.5, px: 1 }}>
                                            <MDTypography variant="caption" fontWeight="bold" color={darkMode ? "white" : "text"}>CPF / CNPJ</MDTypography>
                                        </TableCell>
                                    </TableRow>
                                </TableHead>
                                <TableBody sx={{ display: "table-row-group", width: "100%" }}>
                                    {casosList.map((dev, idx) => (
                                        <TableRow 
                                            key={idx} 
                                            hover
                                            onClick={() => handleOpenDetailModal(dev.id || dev.cvId)}
                                            sx={{ 
                                                cursor: 'pointer',
                                                display: "table-row",
                                                width: "100%",
                                                '&:hover': {
                                                    backgroundColor: darkMode ? '#5A5E6D !important' : '#e3f2fd !important'
                                                }
                                            }}
                                        >
                                            <TableCell sx={{ display: "table-cell", verticalAlign: "middle", py: 1.5, px: 1 }}>
                                                <MDTypography variant="button" fontWeight="bold" color="info">
                                                    {dev.cvId || dev.id}
                                                </MDTypography>
                                            </TableCell>
                                            <TableCell sx={{ display: "table-cell", verticalAlign: "middle", py: 1.5, px: 1 }}>
                                                <MDTypography variant="button" color={darkMode ? "white" : "dark"}>
                                                    {dev.nome || "-"}
                                                </MDTypography>
                                            </TableCell>
                                            <TableCell sx={{ display: "table-cell", verticalAlign: "middle", py: 1.5, px: 1 }}>
                                                <MDTypography variant="caption" color={darkMode ? "white" : "text"}>
                                                    {dev.cpfCnpj || "-"}
                                                </MDTypography>
                                            </TableCell>
                                        </TableRow>
                                    ))}
                                </TableBody>
                            </Table>
                        </TableContainer>
                    )}
                </DialogContent>
                <DialogActions>
                    <MDButton color="secondary" onClick={() => setCasosModalOpen(false)}>
                        Fechar
                    </MDButton>
                </DialogActions>
            </Dialog>

            {/* Modal de Resumo do Devedor */}
            <Dialog
                open={detailModalOpen}
                onClose={() => setDetailModalOpen(false)}
                maxWidth="md"
                fullWidth
                PaperProps={{
                    sx: {
                        backgroundColor: darkMode ? "#4B4F5C" : "#ffffff",
                        color: darkMode ? "#ffffff" : "inherit"
                    }
                }}
            >
                <DialogTitle display="flex" justifyContent="space-between" alignItems="center">
                    <MDTypography variant="h6" color={darkMode ? "white" : "dark"}>
                        Resumo do Devedor {devedorDetail ? `(#${devedorDetail.unencryptedId || devedorDetail.id || ""})` : ""}
                    </MDTypography>
                    <IconButton onClick={() => setDetailModalOpen(false)} size="small" sx={{ color: darkMode ? "#ffffff" : "inherit" }}>
                        <Icon>close</Icon>
                    </IconButton>
                </DialogTitle>
                <DialogContent dividers>
                    {loadingDetail ? (
                        <MDBox display="flex" justifyContent="center" py={4}>
                            <CircularProgress color="info" />
                        </MDBox>
                    ) : !devedorDetail ? (
                        <MDBox py={3} textAlign="center">
                            <MDTypography variant="button" color={darkMode ? "white" : "text"}>
                                Não foi possível carregar as informações deste devedor.
                            </MDTypography>
                        </MDBox>
                    ) : (
                        <MDBox>
                            {/* Dados Básicos em Cards */}
                            <Grid container spacing={2} mb={2}>
                                <Grid item xs={12} sm={6}>
                                    <MDBox p={1.5} sx={{ backgroundColor: darkMode ? "#3A3D47" : "#f8f9fa", borderRadius: '8px', border: darkMode ? "1px solid #5A5E6D" : "1px solid #e0e0e0" }}>
                                        <MDTypography variant="caption" color={darkMode ? "white" : "text"} display="block" opacity={darkMode ? 0.8 : 1}>Nome do Devedor</MDTypography>
                                        <MDTypography variant="button" fontWeight="bold" color={darkMode ? "white" : "dark"}>{devedorDetail.nome || "-"}</MDTypography>
                                    </MDBox>
                                </Grid>
                                <Grid item xs={12} sm={6}>
                                    <MDBox p={1.5} sx={{ backgroundColor: darkMode ? "#3A3D47" : "#f8f9fa", borderRadius: '8px', border: darkMode ? "1px solid #5A5E6D" : "1px solid #e0e0e0" }}>
                                        <MDTypography variant="caption" color={darkMode ? "white" : "text"} display="block" opacity={darkMode ? 0.8 : 1}>CPF / CNPJ</MDTypography>
                                        <MDTypography variant="button" fontWeight="bold" color={darkMode ? "white" : "dark"}>{devedorDetail.documento || "-"}</MDTypography>
                                    </MDBox>
                                </Grid>
                                <Grid item xs={12} sm={6}>
                                    <MDBox p={1.5} sx={{ backgroundColor: darkMode ? "#3A3D47" : "#f8f9fa", borderRadius: '8px', border: darkMode ? "1px solid #5A5E6D" : "1px solid #e0e0e0" }}>
                                        <MDTypography variant="caption" color={darkMode ? "white" : "text"} display="block" opacity={darkMode ? 0.8 : 1}>Status da Ficha</MDTypography>
                                        <MDTypography variant="button" fontWeight="bold" color="info">
                                            {devedorDetail.statusFichaNome || (devedorDetail.fichas && devedorDetail.fichas[0]?.statusFichaNome) || "Sem Status"}
                                        </MDTypography>
                                    </MDBox>
                                </Grid>
                                <Grid item xs={12} sm={6}>
                                    <MDBox p={1.5} sx={{ backgroundColor: darkMode ? "#3A3D47" : "#f8f9fa", borderRadius: '8px', border: darkMode ? "1px solid #5A5E6D" : "1px solid #e0e0e0" }}>
                                        <MDTypography variant="caption" color={darkMode ? "white" : "text"} display="block" opacity={darkMode ? 0.8 : 1}>Agendamento</MDTypography>
                                        <MDTypography variant="button" fontWeight="bold" color={darkMode ? "white" : "dark"}>
                                            {devedorDetail.dataAgendamento ? new Date(devedorDetail.dataAgendamento).toLocaleString('pt-BR') : "Sem agendamento"}
                                        </MDTypography>
                                    </MDBox>
                                </Grid>
                            </Grid>

                            {/* Contratos e Saldos */}
                            <MDTypography variant="button" fontWeight="bold" color={darkMode ? "white" : "text"} display="block" mt={2} mb={1}>
                                Contratos e Saldos
                            </MDTypography>
                            {(!devedorDetail.contratos || devedorDetail.contratos.length === 0) ? (
                                <MDTypography variant="caption" color={darkMode ? "white" : "text"}>Nenhum contrato encontrado.</MDTypography>
                            ) : (
                                <TableContainer component={Paper} elevation={0} sx={{ backgroundColor: 'transparent', boxShadow: 'none', width: '100%' }}>
                                    <Table size="small" sx={{ width: "100%", tableLayout: "fixed", display: "table" }}>
                                        <TableHead sx={{ display: "table-header-group", width: "100%", p: 0 }}>
                                            <TableRow sx={{ display: "table-row", width: "100%" }}>
                                                <TableCell sx={{ width: "45%", display: "table-cell", verticalAlign: "middle", py: 1.5, px: 1 }}>
                                                    <MDTypography variant="caption" fontWeight="bold" color={darkMode ? "white" : "text"}>Contrato / Título</MDTypography>
                                                </TableCell>
                                                <TableCell align="center" sx={{ width: "25%", display: "table-cell", verticalAlign: "middle", py: 1.5, px: 1 }}>
                                                    <MDTypography variant="caption" fontWeight="bold" color={darkMode ? "white" : "text"}>Situação</MDTypography>
                                                </TableCell>
                                                <TableCell align="right" sx={{ width: "30%", display: "table-cell", verticalAlign: "middle", py: 1.5, px: 1 }}>
                                                    <MDTypography variant="caption" fontWeight="bold" color={darkMode ? "white" : "text"}>Saldo (R$)</MDTypography>
                                                </TableCell>
                                            </TableRow>
                                        </TableHead>
                                        <TableBody sx={{ display: "table-row-group", width: "100%" }}>
                                            {devedorDetail.contratos.map((c, idx) => {
                                                const saldoVal = c.saldo != null ? Number(c.saldo) : (c.cvSaldo != null ? Number(c.cvSaldo) : (c.valorOriginal != null ? Number(c.valorOriginal) : 0));
                                                const sit = c.situacao != null ? c.situacao : c.cvSituacao;
                                                return (
                                                    <TableRow key={idx} sx={{ display: "table-row", width: "100%" }}>
                                                        <TableCell sx={{ display: "table-cell", verticalAlign: "middle", py: 1.5, px: 1 }}>
                                                            <MDTypography variant="button" color={darkMode ? "white" : "dark"}>{c.titulo || c.ccTitulo || c.contratoOriginal || `Contrato #${idx + 1}`}</MDTypography>
                                                        </TableCell>
                                                        <TableCell align="center" sx={{ display: "table-cell", verticalAlign: "middle", py: 1.5, px: 1 }}>
                                                            <MDTypography variant="caption" color={sit === 1 || sit === 2 ? "success" : (darkMode ? "white" : "text")}>
                                                                {sit === 1 ? "Ativo" : sit === 2 ? "Baixado Parcial" : sit === 4 ? "Devolvido" : "Inativo"}
                                                            </MDTypography>
                                                        </TableCell>
                                                        <TableCell align="right" sx={{ display: "table-cell", verticalAlign: "middle", py: 1.5, px: 1 }}>
                                                            <MDTypography variant="button" fontWeight="bold" color={darkMode ? "white" : "dark"}>
                                                                {saldoVal.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' })}
                                                            </MDTypography>
                                                        </TableCell>
                                                    </TableRow>
                                                );
                                            })}
                                            <TableRow sx={{ backgroundColor: darkMode ? "#3A3D47" : "#f0f4f8", display: "table-row", width: "100%" }}>
                                                <TableCell colSpan={2} sx={{ display: "table-cell", verticalAlign: "middle", py: 1.5, px: 1 }}>
                                                    <MDTypography variant="button" fontWeight="bold" color={darkMode ? "white" : "dark"}>Saldo Total Ativo:</MDTypography>
                                                </TableCell>
                                                <TableCell align="right" sx={{ display: "table-cell", verticalAlign: "middle", py: 1.5, px: 1 }}>
                                                    <MDTypography variant="button" fontWeight="bold" color="success">
                                                        {devedorDetail.contratos
                                                            .filter(c => {
                                                                const sit = c.situacao != null ? c.situacao : c.cvSituacao;
                                                                return sit === 1 || sit === 2 || sit == null;
                                                            })
                                                            .reduce((acc, c) => {
                                                                const val = c.saldo != null ? Number(c.saldo) : (c.cvSaldo != null ? Number(c.cvSaldo) : (c.valorOriginal != null ? Number(c.valorOriginal) : 0));
                                                                return acc + val;
                                                            }, 0)
                                                            .toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' })}
                                                    </MDTypography>
                                                </TableCell>
                                            </TableRow>
                                        </TableBody>
                                    </Table>
                                </TableContainer>
                            )}
                        </MDBox>
                    )}
                </DialogContent>
                <DialogActions>
                    <MDButton color="secondary" onClick={() => setDetailModalOpen(false)}>
                        Fechar
                    </MDButton>
                </DialogActions>
            </Dialog>
        </Card>
    );
}
